import { msg } from "@omegagrid/localize";
import { ColorTypes } from "@omegagrid/core";
import { ColumnSource, RowSource } from "@omegagrid/grid";
import rp, { GridEditorAdapter, GridEditorOptions } from "@reporting/rp-app-host";
import { StatementStats } from "@reporting/rp-types/definition";

export class ReportStatsGridAdapter extends GridEditorAdapter {

	private _columns: ColumnSource[];

	private stats: StatementStats[];
	private minYear: number;
	private maxYear: number;

	constructor(
		private idServer?: number
	) { super() }

	async getColumns(): Promise<ColumnSource[]> {
		if (this._columns) return this._columns;
		await this.fetchStats();
		this._columns = [{
			data: 'accounting',
			header: '#',
			t: 'n',
			z: '0',
			w: 70,
		}, {
			data: 'company',
			header: msg('Company'),
			t: 't',
			w: 130,
		}, {
			data: 'id_statement',
			header: `# Stat.`,
			t: 'n',
			z: '0',
			w: 70,
		}, {
			data: 'statement',
			header: msg('Statement'),
			t: 't',
			w: 150,
		}, {
			data: 'row_cnt',
			header: msg('Row Count'),
			t: 'n',
			w: 80,
		}];
		for (let year = this.maxYear; year >= this.minYear; year--) {
			this._columns.push({
				data: `year_${year}`,
				header: `${year}`,
				t: 'n',
				w: 80,
			});
		}
		return this._columns;
	}

	async fetchStats() {
		this.stats = await rp.ms('definition').get<StatementStats[]>('statements/stats', { s: this.idServer });
		this.minYear = Math.min(...this.stats.map(s => s.year)) || 2011;
		this.maxYear = Math.max(...this.stats.map(s => s.year)) || 2050;
	}

	async getRows(): Promise<RowSource[]> {
		await this.fetchStats();
		// group stats by accounting + id_statement, merging year columns into a single row
		const map = new Map<string, RowSource>();
		this.stats.forEach(stat => {
			const key = `${stat.accounting}_${stat.id_statement}`;
			let row = map.get(key);
			if (!row) {
				row = {
					data: {
						accounting: stat.accounting,
						company: stat.company,
						id_statement: stat.id_statement,
						statement: stat.statement,
						row_cnt: stat.row_cnt,
					}
				};
				for (let year = this.maxYear; year >= this.minYear; year--) {
					row.data[`year_${year}`] = null;
				}
				map.set(key, row);
			}
			if (stat.year != null) {
				row.data[`year_${stat.year}`] = stat.acct_cnt;
			}
		});
		return Array.from(map.values());
	}

	getOptions(): Partial<GridEditorOptions> {
		return this.createOptions({
			colLetters: false,
			columnSorting: true,
			filters: true,
			pageSize: null,
			editable: false,
			lightTheme: null,
			darkTheme: null,
			exportTypes: ['xlsx'],
			freezeLeft: 5,
			toolbar: {
				save: { enabled: false },
				discard: { enabled: false },
				new: { enabled: false },
				export: {
					text: msg('Export'),
					color: ColorTypes.Gray,
					icon: 'download',
					enabled: true,
					items: [
						{ key: 'export.xlsx', value: msg('Export to {t}', { data: { t: 'XLSX' } }), icon: 'file-excel' }
					]
				}
			},
		});
	}

}
